import { useCallback, useEffect, useRef } from 'react';
import {
  useQuizResultActions,
  useQuizGameState,
  useQuizMetaContext,
  useQuizResults,
  useHasQuizResults,
} from '@mentimeter/quiz';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { GameStateEnums, type InitialGameStates } from '@mentimeter/quiz-types';
import type { QuizOpenStaticContent } from '@core-api/presentation/types/response';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { GameStatesSwitcher } from './GameStateSwitcher';
import { useQuizOpenPreviewResultsLegacy } from './hooks/use-quiz-open-preview-results-legacy';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QuizSlideState = {
  started?: boolean;
  starting?: boolean;
};

export const PresentersPresentationDataHandler = ({
  initialGameState,
  activeSession,
}: {
  initialGameState: InitialGameStates;
  activeSession: number;
}) => {
  const { useQuestion, useSlideState, useConfig, isPreviewResultsEnabled } =
    usePresentationContext();
  const { readonly } = useConfig();
  const slide = useQuestion();

  const staticContent = slide.staticContent as QuizOpenStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const { totalCount, currentIndex } = useQuizMetaContext();
  const { data = [] } = useQuizResults<QuizPlayersScoreData>();
  const quizHasResults = useHasQuizResults();
  const { markAnswer } = useQuizResultActions(slide.slideAdminKey);
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    startQuiz: _startQuiz,
    createQuiz,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
  } = useQuizGameState();
  const [{ started, starting }, setSlideState] =
    useSlideState<QuizSlideState>();

  const startQuiz = useCallback(() => {
    _startQuiz({ countdownToVote: interactiveContent.countdown ?? 0 });
    setSlideState({ started: true, starting: false });
  }, [interactiveContent.countdown, setSlideState, _startQuiz]);

  /**
   * We need to reset our slide state whenever a presenter shows a quiz slide to be able
   * to restart a quiz for example, but only if there are no results. Otherwise the "next"
   * navigation button would say "start quiz" even if there already are results to the
   * specific quiz slide.
   */
  useEffect(() => {
    if (!quizHasResults) {
      setSlideState({ started: false, starting: false });
    }
  }, [setSlideState, quizHasResults]);

  useEffect(() => {
    if (starting && !started) {
      startQuiz();
    }
  }, [startQuiz, started, starting]);

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      createQuiz(sessionChanged ? GameStateEnums.LOBBY : initialGameState);
      createdRef.current = true;
      prevActiveSession.current = activeSession;
    }
  }, [createQuiz, initialGameState, activeSession, gameState]);

  const onReload = useCallback(() => {
    if (connected) {
      createQuiz(initialGameState);
    } else {
      window.location.reload();
    }
  }, [connected, createQuiz, initialGameState]);

  const firstCorrectAnswer = interactiveContent.choices[0]?.title;

  const previewResults = useQuizOpenPreviewResultsLegacy({
    isPreviewResultsEnabled,
    correctAnswer: firstCorrectAnswer,
  });

  const resultsToShow = isPreviewResultsEnabled ? previewResults : data;

  const shouldShowLegacyImage =
    staticContent.image.imageLayoutPosition.start === 0 &&
    staticContent.image.imageLayoutPosition.end === 0;

  return (
    <GameStatesSwitcher
      shouldShowLegacyImage={shouldShowLegacyImage}
      visibleCorrectAnswer={firstCorrectAnswer ?? ''}
      imageUrl={
        slide.images?.[0]?.presets?.original?.url ??
        slide.images?.[0]?.presets?.large?.url
      }
      timeBasedScoring={interactiveContent.scoring === 'time_based'}
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      description={interactiveContent.description}
      textSizeOffset={staticContent.textSizeOffset}
      isReadonly={readonly}
      onMarkAnswer={markAnswer}
      results={resultsToShow}
      isPreviewResults={isPreviewResultsEnabled}
      gameState={gameState}
      timeOffset={timeOffset}
      connectedPlayers={connectedPlayers}
      respondents={respondents}
      onReload={onReload}
      everyoneHasVoted={everyoneHasVoted}
      connectedWithDelay={connectedWithDelay}
      totalQuizQuestions={totalCount}
      currentQuizQuestionIndex={currentIndex}
    />
  );
};
